import { useEffect, useState } from 'react'

export interface DwellOptions<T> {
  /**
   * 밀려도 되는 값(이동 · 대기처럼 그 자체로는 말할 것이 없는 자리). 줄이 넘치면 **이 값들을 먼저** 버린다 —
   * 동작 자리(분주 · 폐기 · 팁 장착)는 끝까지 보여 준다
   */
  skippable?: ((value: T) => boolean) | undefined
  /** 기다리는 값의 상한. 넘치면 밀려도 되는 값 → 가장 오래된 값 차례로 버린다(그림이 서버보다 `maxQueue × minMs` 넘게 뒤지지 않게) */
  maxQueue?: number | undefined
}

const DEFAULT_MAX_QUEUE = 4

/**
 * **최소 머무름** — 값이 바뀌어도 직전 값을 `minMs` 는 보여 준 뒤에 바꾼다. 🔴 그 사이 바뀐 값들은 **건너뛰지 않고 차례로**(줄) 보여 준다.
 *
 * 염색기 피펫 툴처럼 서버 동작이 0.5~1.7초마다 바뀌면(dev2 2026-10-08 실측) 이동이 너무 빨라 눈에 안 들어온다(eric 「너무 빨라서 안 보인다는
 * 거야? 딜레이 줄 수 있어?」). 종전(2026-10-08 오전)엔 머무름이 끝나면 **마지막 값으로 뛰어** 그 사이의 분주 · 폐기(`expel` · `discard_tip`)가
 * 통째로 사라졌다(eric 「expel 이게 다 있는데 왜 그림으로 표현이 안 돼?」 · 「discard tip 하면 폐기통에 왔다 가야지」). 이제는 서버가 말한
 * 자리를 **하나씩 전부**, 각각 `minMs` 씩 보여 준다. 🔴 지어내는 것이 아니다 — 서버가 실제로 말한 자리들을 조금 늦게 보여 줄 뿐이고,
 * 마지막에는 늘 서버의 현재 값에 닿는다. 폴링도 아니다 — 타이머는 다음 값으로 넘어가는 한 번뿐이다.
 *
 * 줄이 `maxQueue` 를 넘으면 밀려도 되는 값(`skippable` · 이동 · 대기)부터, 그래도 넘치면 가장 오래된 값을 버린다 — 그림이 서버보다
 * 몇 초 넘게 뒤지지 않게.
 */
export function useDwell<T>(
  value: T,
  minMs: number,
  same: (a: T, b: T) => boolean,
  options: DwellOptions<T> = {},
): T {
  const { skippable, maxQueue = DEFAULT_MAX_QUEUE } = options
  const [shown, setShown] = useState(value)
  const [since, setSince] = useState(() => Date.now())
  const [queue, setQueue] = useState<readonly T[]>([])

  /*
   * 렌더 중 파생 상태 갱신(React 허용 패턴) — ⓐ 버리기 끝에 머리가 보이는 값과 같아졌으면 넘긴다(같은 자리를 두 번 머물지 않는다)
   * ⓑ 마지막으로 줄 선 값(없으면 보이는 값)과 다르면 줄에 붙인다
   */
  let head = 0
  while (head < queue.length && same(queue[head] as T, shown)) head += 1
  if (head > 0) setQueue(queue.slice(head))
  const tail = queue.length === 0 ? shown : (queue[queue.length - 1] as T)
  if (head === 0 && !same(tail, value)) {
    const next = [...queue, value]
    while (next.length > maxQueue) {
      const drop =
        skippable === undefined
          ? -1
          : next.findIndex((item, index) => index < next.length - 1 && skippable(item))
      next.splice(drop < 0 ? 0 : drop, 1)
    }
    setQueue(next)
  }

  useEffect(() => {
    const head = queue[0]
    if (queue.length === 0 || head === undefined) return
    const wait = Math.max(0, since + minMs - Date.now())
    const id = setTimeout(() => {
      setShown(head)
      setSince(Date.now())
      setQueue((prev) => prev.slice(1))
    }, wait)
    return () => clearTimeout(id)
  }, [queue, since, minMs])
  return shown
}
